import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clientQuoteService from '../services/clientQuoteService';
import { useNotification } from '../context/NotificationContext';
import AuthContext from '../context/AuthContext';
import { formatInstantDate } from '../utils/datetime';
import ConfirmActionModal from '../components/ConfirmActionModal';
import SortableHeader from '../components/SortableHeader';
import '../styles/general.css';

const ClientQuoteListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { timezone, canManage } = useContext(AuthContext);
  // Write actions are hidden, not disabled: a button the user can never
  // enable is its own kind of noise. The API rejects them regardless.
  const canWrite = canManage && canManage('quotes');
  const [clientQuotes, setClientQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState('-created_at');
  const [filterField, setFilterField] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [convertingQuote, setConvertingQuote] = useState(null);
  const [converting, setConverting] = useState(false);
  const [exportingId, setExportingId] = useState(null);

  const perPage = 25;
  const totalPages = Math.ceil(totalCount / perPage);

  const loadClientQuotes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await clientQuoteService.listClientQuotes(
        filterField || null,
        filterValue || null,
        sortBy,
        page,
        createdFrom || null,
        createdTo || null
      );
      setClientQuotes(res.data.results || []);
      setTotalCount(res.data.count || 0);
    } catch (err) {
      console.error('Error loading Client Quotes:', err);
      notify.error(t('clientQuotes.failedToLoadQuotes'));
    } finally {
      setLoading(false);
    }
  }, [page, sortBy, filterField, filterValue, createdFrom, createdTo, notify, t]);

  useEffect(() => {
    loadClientQuotes();
  }, [loadClientQuotes]);

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this Client Quote?')) {
      clientQuoteService.deleteClientQuote(id)
        .then(() => {
          loadClientQuotes();
        })
        .catch(err => {
          notify.error(t('clientQuotes.failedToDeleteQuote'));
          console.error(err);
        });
    }
  };

  const handleFilterChange = (field, value) => {
    setFilterField(field);
    setFilterValue(value);
    setPage(1);
  };

  const handleCreatedFromChange = (value) => {
    setCreatedFrom(value);
    setPage(1);
  };

  const handleCreatedToChange = (value) => {
    setCreatedTo(value);
    setPage(1);
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortBy(`-${field}`);
    } else if (sortBy === `-${field}`) {
      setSortBy(field);
    } else {
      setSortBy(`-${field}`);
    }
    setPage(1);
  };

  const handleConvertClick = (quote) => {
    setConvertingQuote(quote);
  };

  const handleConvertConfirm = async () => {
    if (!convertingQuote) return;
    setConverting(true);
    try {
      await clientQuoteService.convertToSale(convertingQuote.id);
      notify.success(t('clientQuotes.quoteConverted'));
      setConvertingQuote(null);
      loadClientQuotes();
    } catch (err) {
      console.error('Error converting quote to sale:', err);
      notify.error(t('clientQuotes.failedToConvertQuote'));
    } finally {
      setConverting(false);
    }
  };

  const handleExportPDF = async (id) => {
    // Unlike the form page, nothing is saved first: the list never holds
    // unsaved edits, so the stored quote is already what the PDF should show.
    setExportingId(id);
    try {
      await clientQuoteService.exportPDF(id);
    } catch (err) {
      console.error('Error exporting PDF:', err);
      notify.error(t('clientQuotes.failedToExportPDF'));
    } finally {
      setExportingId(null);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };


  if (loading && clientQuotes.length === 0) {
    return (
      <div className="loading">
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="page-container-wide">
      <div className="page-header">
        <h1>{t('clientQuotes.title')}</h1>
        {canWrite && (
          <button
            onClick={() => navigate('/client-quotes/create')}
            className="btn btn-primary"
          >
            {t('clientQuotes.createButton')}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="filter-panel">
        <div className="filter-row">
          <div className="filter-group">
            <label className="form-label">
              {t('filters.filterBy')}
            </label>
            <select
              value={filterField}
              onChange={(e) => handleFilterChange(e.target.value, filterValue)}
              className="form-select"
            >
              <option value="">{t('filters.none')}</option>
              <option value="quote_number">{t('clientQuotes.filters.quoteNumber')}</option>
              <option value="client">{t('table.client')}</option>
              <option value="buyer">{t('table.buyer')}</option>
              <option value="status">{t('clientQuotes.filters.statusLabel')}</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="form-label">
              {t('filters.searchValue')}
            </label>
            {filterField === 'status' ? (
              <select
                value={filterValue}
                onChange={(e) => handleFilterChange(filterField, e.target.value)}
                className="form-select"
              >
                <option value="">{t('filters.none')}</option>
                <option value="cotizacion">{t('clientQuotes.status.cotizacion')}</option>
                <option value="venta">{t('clientQuotes.status.venta')}</option>
                <option value="cancelado">{t('clientQuotes.status.cancelado')}</option>
              </select>
            ) : (
              <input
                type="text"
                value={filterValue}
                onChange={(e) => handleFilterChange(filterField, e.target.value)}
                placeholder={t('filters.searchValue')}
                className="form-input"
              />
            )}
          </div>

          <div className="filter-group">
            <label className="form-label" htmlFor="clientQuoteCreatedFrom">
              {t('filters.createdFrom')}
            </label>
            <input
              id="clientQuoteCreatedFrom"
              type="date"
              value={createdFrom}
              onChange={(e) => handleCreatedFromChange(e.target.value)}
              className="form-input"
            />
          </div>

          <div className="filter-group">
            <label className="form-label" htmlFor="clientQuoteCreatedTo">
              {t('filters.createdTo')}
            </label>
            <input
              id="clientQuoteCreatedTo"
              type="date"
              value={createdTo}
              onChange={(e) => handleCreatedToChange(e.target.value)}
              className="form-input"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table-wrapper">
        <table className="table">
          <thead>
            <tr>
              <SortableHeader field="quote_number" label={t('clientQuotes.listHeaders.quoteNumber')} sortBy={sortBy} onSort={handleSortChange} />
              <SortableHeader field="client" label={t('table.client')} sortBy={sortBy} onSort={handleSortChange} />
              <SortableHeader field="buyer" label={t('table.buyer')} sortBy={sortBy} onSort={handleSortChange} />
              <SortableHeader field="updated_at" label={t('table.updated')} sortBy={sortBy} onSort={handleSortChange} className="text-center" />
              <th className="text-center">{t('table.createdBy')}</th>
              <th className="text-center">{t('common.status')}</th>
              <SortableHeader field="grandTotal" label={t('table.total')} sortBy={sortBy} onSort={handleSortChange} className="text-right" />
              <th className="text-center">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {clientQuotes.length === 0 ? (
              <tr>
                <td colSpan="8" className="empty-state">
                  {t('clientQuotes.noQuotesYet')}
                </td>
              </tr>
            ) : (
              clientQuotes.map((quote) => (
                <tr key={quote.id}>
                  <td>#{quote.quote_number}</td>
                  <td>{quote.client_name}</td>
                  <td>{quote.buyer_contact?.name}</td>
                  <td className="text-center">{formatInstantDate(quote.updated_at, timezone)}</td>
                  <td className="text-center">{quote.created_by_email}</td>
                  <td className="text-center">
                    <span className={`table-status ${quote.status}`}>
                      {t(`clientQuotes.status.${quote.status}`)}
                    </span>
                    {quote.active_sale && (
                      <div>
                        <a
                          href={`/sales/${quote.active_sale.id}`}
                          onClick={(e) => { e.preventDefault(); navigate(`/sales/${quote.active_sale.id}`); }}
                        >
                          {quote.active_sale.sale_number}
                        </a>
                      </div>
                    )}
                  </td>
                  <td className="text-right">{formatCurrency(quote.grandTotal)}</td>
                  <td className="text-center table-actions">
                    {canWrite && quote.status !== 'venta' && (
                      <button
                        onClick={() => navigate(`/client-quotes/${quote.id}/edit`)}
                        className="btn btn-primary btn-small"
                      >
                        {t('common.edit')}
                      </button>
                    )}
                    {quote.status === 'cotizacion' && (
                      <button
                        onClick={() => handleConvertClick(quote)}
                        className="btn btn-primary btn-small"
                      >
                        {t('clientQuotes.convertToSale')}
                      </button>
                    )}
                    {/* Exporting is a read of a quote the user can already
                        see, so it is not gated on canWrite and stays
                        available once the quote is a sale. */}
                    <button
                      onClick={() => handleExportPDF(quote.id)}
                      disabled={exportingId === quote.id}
                      className="btn btn-export btn-small"
                    >
                      {t('clientQuotes.exportPDF')}
                    </button>
                    {canWrite && quote.status !== 'venta' && (
                      <button
                        onClick={() => handleDelete(quote.id)}
                        className="btn btn-danger btn-small"
                      >
                        {t('common.delete')}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="btn btn-primary"
          >
            {t('common.previous')}
          </button>
          <span className="pagination-info">
            {t('clientQuotes.pagination.pageOf', { page, total: totalPages })}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="btn btn-primary"
          >
            {t('common.next')}
          </button>
        </div>
      )}

      <ConfirmActionModal
        isOpen={!!convertingQuote}
        onClose={() => setConvertingQuote(null)}
        onConfirm={handleConvertConfirm}
        title={t('clientQuotes.convertToSale')}
        message={t('clientQuotes.confirmConvertToSale')}
        confirmLabel={t('common.confirm')}
        confirming={converting}
      />
    </div>
  );
};

export default ClientQuoteListPage;
